import { getToken, logout, getAuthHeader } from '../utils/authUtils';

const API_BASE_URL = process.env.REACT_APP_API_URL+"/api";

export const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;
  
  const headers = {
    "Content-Type": "application/json",
    ...getAuthHeader(),
    ...(options.headers || {}),
  };
  
  try {
    const response = await fetch(url, {
      ...options,
      headers,
    });
    
    const contentType = response.headers.get("content-type");
    const isJson = contentType && contentType.includes("application/json");
    
    let data;
    if (isJson) {
      data = await response.json();
    } else {
      data = await response.text();
    }
    
    if (response.status === 401) {
      logout();
      return null;
    }
    
    if (!response.ok) {
      const errorMessage = isJson && data.message ? data.message : `API request failed with status ${response.status}`;
      throw new Error(errorMessage);
    }
    
    return { data, response };
  } catch (error) {
    console.error("API request error:", error);
    throw error;
  }
};

export const authAPI = {
  login: async (credentials) => {
    try {
      if (!credentials.email || !credentials.password) {
        throw new Error("Email and password are required");
      }
      
      // For login, we need to handle errors differently - don't use apiRequest
      // because it will call logout() on 401, which causes a page reload
      const url = `${API_BASE_URL}/users/login`;
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(credentials),
      });
      
      const contentType = response.headers.get("content-type");
      const isJson = contentType && contentType.includes("application/json");
      
      let data;
      if (isJson) {
        data = await response.json();
      } else {
        data = await response.text();
      }
      
      if (!response.ok) {
        // For login endpoint, treat 401 as invalid credentials, not as logout trigger
        const errorMessage = isJson && data.message 
          ? data.message 
          : response.status === 401
          ? "Invalid credentials. Please check your email and password."
          : `Login failed with status ${response.status}`;
        throw new Error(errorMessage);
      }
      
      return data; 
    } catch (error) {
      throw error;
    }
  },
  
};

export const dashboardAPI = {
  getDashboardCount: async () => {
    try {
      const { data } = await apiRequest("/users/getdashboardcount", {
        method: "GET"
      });
      return data;
    } catch (error) {
      console.error("Error fetching dashboard count:", error);
      throw error;
    }
  }
};

export const contactAPI = {
  getContacts: async (page = 1, limit = 10, queryParams = '') => {
    try {
      const query = queryParams || `page=${page}&limit=${limit}`;
      
      const { data } = await apiRequest(`/contact?${query}`, {
        method: "GET"
      });
      return data;
    } catch (error) {
      throw error;
    }
  },

  updateContactBulk: async (payload) => {
  try {
    const { data } = await apiRequest(`/contact/updateContact`, {
      method: "PATCH",
      body: JSON.stringify(payload),
    });
    return data;
  } catch (error) {
    console.error("Error updating contacts:", error);
    throw error;
  }
},

   getUsers: async () => {
    try {
      const { data } = await apiRequest('/users', {
        method: "GET"
      });
      return data;
    } catch (error) {
      console.error("Error fetching users:", error);
      throw error;
    }
  },

  deleteContacts: async (ids) => {
  try {
    const { data } = await apiRequest("/contact/deleteContacts", {
      method: "POST",
      body: JSON.stringify({ ids }),
    });
    return data;
  } catch (error) {
    throw error;
  }
},

  importContacts: async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const headers = {
        ...getAuthHeader()
      };
      
      const response = await fetch(`${API_BASE_URL}/contact/import`, {
        method: "POST",
        headers,
        body: formData,
      });
      
      const contentType = response.headers.get("content-type");
      const isJson = contentType && contentType.includes("application/json");
      
      let data;
      if (isJson) {
        data = await response.json();
      } else {
        data = await response.text();
      }
      
      if (response.status === 401) {
        logout();
        return null;
      }
      
      if (!response.ok) {
        const errorMessage = isJson && data.message ? data.message : `Import failed with status ${response.status}`;
        throw new Error(errorMessage);
      }
      
      return data;
    } catch (error) {
      console.error("Error importing contacts:", error);
      throw error;
    }
  },
  
  addContact: async (contactData) => {
    try {
      const { data } = await apiRequest("/contact", {
        method: "POST",
        body: JSON.stringify(contactData),
      });
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  updateContact: async (id, contactData) => {
    try {
      const { data } = await apiRequest(`/contact/${id}`, {
        method: "PATCH",
        body: JSON.stringify(contactData),
      });
      return data;
    } catch (error) {
      throw error;
    }
  },
  
  deleteContact: async (id) => {
    try {
      const { data } = await apiRequest(`/contact/${id}`, {
        method: "DELETE",
      });
      return data;
    } catch (error) {
      throw error;
    }
  },

  downloadContactsCSV: async (payload) => {
    try {
      const url = `${API_BASE_URL}/contact/download/csv`;
      const headers = {
        "Content-Type": "application/json",
        ...getAuthHeader(),
      };
      
      const response = await fetch(url, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });
      
      if (response.status === 401) {
        logout();
        return null;
      }
      
      if (!response.ok) {
        const contentType = response.headers.get("content-type");
        const isJson = contentType && contentType.includes("application/json");
        let errorData;
        if (isJson) {
          errorData = await response.json();
        } else {
          errorData = await response.text();
        }
        const errorMessage = isJson && errorData.message ? errorData.message : `Download failed with status ${response.status}`;
        throw new Error(errorMessage);
      }
      
      // Get the blob from response
      const blob = await response.blob();
      
      // Create a download link
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `contacts_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      return { success: true };
    } catch (error) {
      console.error("Error downloading contacts CSV:", error);
      throw error;
    }
  }
};

export const notesAPI = {
  getNotes: async (contactId) => {
    try {
      if (!contactId) {
        throw new Error("Contact ID is required");
      }
      
      const { data } = await apiRequest(`/notes?contactId=${contactId}`, {
        method: "GET"
      });
      return data;
    } catch (error) {
      console.error("Error fetching notes:", error);
      throw error;
    }
  },
  
  addNote: async (noteData) => {
    try {
      if (!noteData.contactId || !noteData.content) {
        throw new Error("Contact ID and content are required");
      }
      
      const { data } = await apiRequest("/notes", {
        method: "POST",
        body: JSON.stringify(noteData),
      });
      return data;
    } catch (error) {
      console.error("Error adding note:", error);
      throw error;
    }
  },
  
  updateNote: async (noteId, noteData) => {
    try {
      const { data } = await apiRequest(`/notes/${noteId}`, {
        method: "PATCH",
        body: JSON.stringify(noteData),
      });
      return data;
    } catch (error) {
      console.error("Error updating note:", error);
      throw error;
    }
  },
  
  deleteNote: async (noteId) => {
    try {
      const { data } = await apiRequest(`/notes/${noteId}`, {
        method: "DELETE",
      });
      return data;
    } catch (error) {
      console.error("Error deleting note:", error);
      throw error;
    }
  }
};

export const contactsAPI = {
  getContact: async (contactId) => {
    try {
      const { data } = await apiRequest(`/contact/${contactId}`, {
        method: "GET"
      });
      return data;
    } catch (error) {
      console.error("Error fetching contact:", error);
      throw error;
    }
  },
  
  updateContact: async (contactId, contactData) => {
    try {
      const { data } = await apiRequest(`/contact/${contactId}`, {
        method: "PATCH",
        body: JSON.stringify(contactData),
      });
      return data;
    } catch (error) {
      console.error("Error updating contact:", error);
      throw error;
    }
  }
};

export const salesLeadAPI = {
getSalesLeads: async (page = 1, limit = 50, queryString = '') => {
  try {
    const url = queryString ? `/sales-lead?${queryString}` : `/sales-lead?page=${page}&limit=${limit}`;
    const { data } = await apiRequest(url, { method: "GET" });
    return data;
  } catch (error) {
    console.error("Error fetching sales leads:", error);
    throw error;
  }
},

  getSalesLead: async (id) => {
    try {
      const { data } = await apiRequest(`/sales-lead/${id}`, { method: "GET" });
      return data;
    } catch (error) {
      console.error("Error fetching sales lead:", error);
      throw error;
    }
  },

  updateSalesLeadStatus: async (id, status) => {
    try {
      const { data } = await apiRequest(`/sales-lead/${id}`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
      return data;
    } catch (error) {
      console.error("Error updating sales lead status:", error);
      throw error;
    }
  },

  updateSalesLeadEngagement: async (id, engagementData) => {
    try {
      const { data } = await apiRequest(`/sales-lead/${id}`, {
        method: "PATCH",
        body: JSON.stringify(engagementData),
      });
      return data;
    } catch (error) {
      console.error("Error updating sales lead engagement:", error);
      throw error;
    }
  }
};

export const salesTargetAPI = {

  setSalesTarget: async (targetData) => {
    try {
      const { data } = await apiRequest('/sales-target', {
        method: "POST",
        body: JSON.stringify(targetData),
      });
      return data;
    } catch (error) {
      console.error("Error setting sales target:", error);
      throw error;
    }
  },


};

export const updateLeadToClosedWon = async (leadId, closedWonData) => {
  try {
    const { data } = await apiRequest(`/sales-lead/${leadId}`, {
      method: "PATCH",
      body: JSON.stringify({
        status: "closed_won",
        amount: closedWonData.amount,
        comment: closedWonData.comment,
        contact_id: closedWonData.contact_id
      }),
    });
    return data;
  } catch (error) {
    console.error("Error updating lead to closed won:", error);
    throw error;
  }
};

export const updateLeadToPilotPlan = async (leadId, pilotPlanData) => {
  try {
    const { data } = await apiRequest(`/sales-lead/${leadId}`, {
      method: "PATCH",
      body: JSON.stringify({
        status: "pilot_plan",
        amount: pilotPlanData.amount,
        comment: pilotPlanData.comment,
        contact_id: pilotPlanData.contact_id
      }),
    });
    return data;
  } catch (error) {
    console.error("Error updating lead to pilot plan:", error);
    throw error;
  }
};

export const closingAPI = {
  getClosingLeads: async (page = 1, limit = 10, queryString = '') => {
    try {
      const url = queryString ? `/closing?${queryString}` : `/closing?page=${page}&limit=${limit}`;
      const { data } = await apiRequest(url, { method: "GET" });
      return data;
    } catch (error) {
      console.error("Error fetching closing leads:", error);
      throw error;
    }
  },

  createClosingLead: async (payload, userId = null) => {
    try {
      console.log('Payload:', payload);
      console.log('User ID received:', userId);
      
      const finalPayload = {
        ...payload,
        user_id: userId  
      };
      
      console.log('Final payload with user_id:', finalPayload);
      
      const { data } = await apiRequest('/closing', { 
        method: "POST",
        body: JSON.stringify(finalPayload),
      });
      return data;
    } catch (error) {
      console.error("Error creating closing lead:", error);
      throw error;
    }
  }
};

export default {
  apiRequest,
  authAPI,
  dashboardAPI,
  contactAPI,
  notesAPI,
  contactsAPI,
  salesLeadAPI
};